import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { missionService, matrixService, leaveMatrixBeacon } from '../services/api';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useLocalizedContent, getShortDescription, getFullDescription } from '../utils/language';
import ReactMarkdown from 'react-markdown';
import { mostraDataHora } from '../utils/dates';
import BlocatgeCopia from '../components/BlocatgeCopia';

// Quan queda aquest temps, s'avisa l'alumne. El cronòmetre ja es posa taronja
// als 10 minuts i vermell als 5; això és l'avís que no cal estar mirant.
const AVIS_SEGONS = 300;

// L'escala de valoració de l'ACH, en ordre.
//
// És una escala ORDINAL i la pantalla ho ha de dir. Fins al 05/08/2026 cada
// cel·la era un desplegable de 60 px: no es veia que hi hagués un ordre, ni de
// quin costat queia cada valor, i valorar una matriu de 6 hipòtesis × 14
// evidències eren 84 obertures de llista. En filera, es valora amb un sol clic
// i l'escala sencera és a la vista.
const VALORACIONS = ['CC', 'C', 'N', 'I', 'II'];

// El nom sencer de cada valor, per al text emergent de cada botó.
const NOM_VALORACIO = {
  CC: 'matrix.veryConsistent',
  C: 'matrix.consistent',
  N: 'matrix.neutral',
  I: 'matrix.inconsistent',
  II: 'matrix.veryInconsistent',
};

function Matrix() {
  const { missionId } = useParams();
  const { t, i18n } = useTranslation();
  const { isInstructor } = useAuth();
  const getLocalizedText = useLocalizedContent();
  const [mission, setMission] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLegend, setShowLegend] = useState(false);
  // L'enunciat és plegable. Comença obert —qui encara no ha començat l'ha de
  // llegir— i es plega tot sol quan l'exercici ja està en marxa, perquè llavors
  // la feina és creuar evidència amb hipòtesi i la matriu ha de ser el primer
  // que es veu. Es torna a obrir amb un clic i queda com l'alumne el deixi.
  const [briefingObert, setBriefingObert] = useState(true);
  const [descriptionModal, setDescriptionModal] = useState(null);

  // La fitxa d'una hipòtesi o d'una evidència.
  //
  // Rep l'element sencer i no quatre cadenes soltes, perquè d'una evidència
  // hi ha dues coses més que l'alumne ha de poder mirar i que fins al
  // 06/08/2026 no sortien enlloc de la pantalla de l'examen: d'on surt i com
  // de fiable és. En l'ACH, el pes d'una evidència depèn de la seva font.
  const obreFitxa = (tipus, item) => {
    setDescriptionModal({
      tipus,
      code: item.code,
      titol: getShortDescription(item, i18n.language),
      text: getFullDescription(item, i18n.language),
      font: getLocalizedText(item.source),
      fiabilitat: typeof item.reliability === 'number' ? item.reliability : null,
    });
  };

  const closeDescriptionModal = () => {
    setDescriptionModal(null);
  };

  // La fitxa es tanca amb Escape. S'obre i es tanca desenes de vegades durant
  // un examen: obligar a buscar la creu amb el ratolí cada cop és temps.
  useEffect(() => {
    if (!descriptionModal) return;
    const ambEscape = (e) => {
      if (e.key === 'Escape') setDescriptionModal(null);
    };
    window.addEventListener('keydown', ambEscape);
    return () => window.removeEventListener('keydown', ambEscape);
  }, [descriptionModal]);

  // Mentre s'escriu la conclusió, la matriu ha marxat de la pantalla i el que
  // fa falta no són les 84 cel·les: són les puntuacions i la tria. La barra de
  // dalt s'hi afegeix una segona línia.
  //
  // El que es vigila és la REDACCIÓ, no la taula, i amb un marge de 120 px.
  // Vigilant la taula, el senyal s'autodestruïa: la barra és enganxada però
  // ocupa lloc, o sigui que en créixer 36 px empeny tot el que ve a sota; just
  // al llindar, ensenyar la tira tornava la taula a la pantalla, la tira
  // s'amagava, la taula tornava a marxar... i el parpelleig no s'acabava mai.
  // Un bloc de 400 px que ha d'entrar 120 dins de la pantalla no es mou prou
  // amb aquests 36 px per sortir-ne.
  const taulaRef = React.useRef(null);
  const redaccioRef = React.useRef(null);
  const [escrivint, setEscrivint] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [isOnline, setIsOnline] = useState(true);

  // Com va el desat, per dir-ho sense un botó.
  //
  // Hi havia un botó «Desar progrés» que era de quan el desat automàtic no
  // existia: des del 05/08/2026 la redacció es desa sola cinc segons després
  // de deixar d'escriure. Un botó de desar al costat d'un camp que ja es desa
  // sol no tranquil·litza ningú —fa dubtar de si cal prémer-lo—. El que fa
  // falta és saber si està desat, i només reapareix un botó quan el desat
  // falla, que és l'únic moment en què l'alumne hi pot fer alguna cosa.
  const [estatDesat, setEstatDesat] = useState('inactiu');

  // New submission components state
  const [assessment, setAssessment] = useState('');
  const [selectedHypothesis, setSelectedHypothesis] = useState('');
  const [diagnosticityRanking, setDiagnosticityRanking] = useState([]);

  // La hipòtesi que l'alumne s'afegeix: un sol camp, un sol idioma.
  const [showAddHypothesisModal, setShowAddHypothesisModal] = useState(false);
  const [novaHipotesi, setNovaHipotesi] = useState('');
  const [novaAmpliacio, setNovaAmpliacio] = useState('');

  useEffect(() => {
    initializeMatrix();
  }, [missionId]);

  // Senyal de vida cap al servidor.
  //
  // Substitueix el sincronisme de 30 s que hi havia fins al 03/08/2026. La
  // diferència de fons no és la freqüència: és qui mana. Abans el navegador
  // decidia quan s'havia acabat el temps i s'autolliurava tot sol, de manera que
  // tancar la pestanya deixava la matriu oberta per sempre. Ara el rellotge és
  // del servidor i això només l'hi diu que el navegador segueix viu.
  //
  // Es dispara sol, tant si l'alumne escriu com si fa deu minuts que mira la
  // pantalla pensant: mesura si el NAVEGADOR hi és, no si l'usuari es mou.
  useEffect(() => {
    if (!matrix?.id || !matrix.is_started || matrix.status !== 'in_progress') {
      return;
    }

    let cancelled = false;

    const beat = async () => {
      try {
        const { data } = await matrixService.heartbeat(matrix.id);
        if (cancelled) return;
        setIsOnline(true);
        setTimeRemaining(data.time_remaining_seconds);

        // El servidor ha canviat l'estat (temps exhaurit, o congelada perquè
        // en algun moment vam desaparèixer): cal rellegir-ho tot.
        if (data.status !== matrix.status || data.is_frozen !== matrix.is_frozen) {
          const fresh = await matrixService.getMatrix(matrix.id);
          if (!cancelled) setMatrix(fresh.data);
        }
      } catch (error) {
        if (!cancelled) setIsOnline(false);
      }
    };

    beat();
    const interval = setInterval(beat, (matrix.heartbeat_seconds || 20) * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [matrix?.id, matrix?.is_started, matrix?.status, matrix?.is_frozen, matrix?.heartbeat_seconds]);

  // Compte enrere visual. No decideix res: només fa que el número baixi de
  // manera fluida entre senyals de vida. Qui diu quan s'ha acabat és el servidor.
  useEffect(() => {
    if (!matrix?.is_started || matrix.status !== 'in_progress' || matrix.is_frozen) {
      return;
    }

    if (timeRemaining === null && matrix.time_remaining_seconds !== null) {
      setTimeRemaining(matrix.time_remaining_seconds);
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev === null || prev === undefined) return prev;
        return Math.max(0, prev - 1);
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [matrix?.is_started, matrix?.status, matrix?.is_frozen]);

  // Avís dels cinc minuts.
  //
  // Fins ara l'única pista que el temps s'acabava era que el número del
  // cronòmetre canviava de color, i qui està escrivint la conclusió no mira el
  // cronòmetre. L'avís salta un sol cop per exercici: es rearma només si el
  // temps torna a pujar per damunt del llindar, que és el que passa quan
  // l'instructor reobre o reinicia.
  //
  // És una FINESTRA que s'ha d'acceptar, no una notificació (06/08/2026). Un
  // avís que s'esvaeix sol al cap de vuit segons se'l perd qui està escrivint i
  // mirant el teclat, que és exactament qui l'ha de rebre. Interromp a posta.
  const avisCincMinuts = React.useRef(false);
  const [avisTempsObert, setAvisTempsObert] = useState(false);
  useEffect(() => {
    if (timeRemaining === null || timeRemaining === undefined) return;
    if (matrix?.status !== 'in_progress' || matrix?.is_frozen) return;

    if (timeRemaining > AVIS_SEGONS) {
      avisCincMinuts.current = false;
      return;
    }

    if (timeRemaining > 0 && !avisCincMinuts.current) {
      avisCincMinuts.current = true;
      setAvisTempsObert(true);
    }
  }, [timeRemaining, matrix?.status, matrix?.is_frozen, t]);

  // Quan el compte visual toca zero, demanem l'estat de seguida en comptes
  // d'esperar el senyal següent: així la pantalla es tanca quan toca i no fins a
  // vint segons després.
  useEffect(() => {
    if (timeRemaining !== 0 || !matrix?.id || matrix.status !== 'in_progress') return;
    (async () => {
      try {
        const { data } = await matrixService.heartbeat(matrix.id);
        if (data.status !== matrix.status) {
          const fresh = await matrixService.getMatrix(matrix.id);
          setMatrix(fresh.data);
          toast.error(
            t('matrix.timeExpiredAutoSubmitted') ||
            "S'ha acabat el temps. L'exercici s'ha tancat automàticament."
          );
        }
      } catch (error) {
        setIsOnline(false);
      }
    })();
  }, [timeRemaining, matrix?.id, matrix?.status]);

  // Avís de marxa quan es tanca la pestanya.
  //
  // S'escolta 'pagehide' i NO 'visibilitychange': canviar de pestanya o
  // minimitzar no ha de congelar res, perquè l'alumne pot estar consultant una
  // altra cosa mentre treballa. Només compta que la pàgina desaparegui de debò.
  useEffect(() => {
    if (!matrix?.id || !matrix.is_started || matrix.status !== 'in_progress' || matrix.is_frozen) {
      return;
    }
    const avisar = () => leaveMatrixBeacon(matrix.id);
    window.addEventListener('pagehide', avisar);
    return () => window.removeEventListener('pagehide', avisar);
  }, [matrix?.id, matrix?.is_started, matrix?.status, matrix?.is_frozen]);

  // Desat automàtic de la redacció.
  //
  // El text de l'avaluació vivia només a la memòria del navegador fins que
  // l'alumne premia «desar». Qualsevol cosa que el tragués de la pàgina —abans,
  // la caducitat del token als 30 minuts— se l'enduia. Ara es desa sol cinc
  // segons després de deixar d'escriure.
  useEffect(() => {
    if (!matrix?.id || !matrix.is_started) return;
    if (matrix.status !== 'in_progress' || matrix.is_frozen) return;

    // Es comparen els TRES camps que updateMatrixAnalysis envia, no només el
    // text. Mirant només `assessment`, qui triava la hipòtesi més probable o
    // ordenava l'evidència sense escriure res no desava mai, i si després
    // vencia el temps el servidor rebutjava el desat i la tria es perdia: just
    // el que aquest desat automàtic havia d'evitar.
    const senseCanvis =
      assessment === (matrix.conclusion || '') &&
      (selectedHypothesis || '') === (matrix.most_likely_hypothesis_key || '') &&
      JSON.stringify(diagnosticityRanking) === JSON.stringify(matrix.diagnosticity_ranking || []);
    if (senseCanvis) return;

    setEstatDesat((previ) => (previ === 'desant' ? previ : 'pendent'));

    const desar = setTimeout(() => {
      updateMatrixAnalysis({ silent: true });
    }, 5000);
    return () => clearTimeout(desar);
  }, [assessment, selectedHypothesis, diagnosticityRanking, matrix?.id, matrix?.status, matrix?.is_frozen]);

  useEffect(() => {
    const node = redaccioRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const observador = new IntersectionObserver(
      ([entrada]) => setEscrivint(entrada.isIntersecting),
      { rootMargin: '0px 0px -120px 0px' }
    );
    observador.observe(node);
    return () => observador.disconnect();
  }, [matrix?.id, matrix?.is_started, matrix?.status]);

  const initializeMatrix = async () => {
    try {
      // Fetch mission details
      const missionResponse = await missionService.getMission(missionId);
      const missionData = missionResponse.data;
      setMission(missionData);

      // Check if mission has hypotheses and evidence
      if (!missionData.hypotheses?.length || !missionData.evidence?.length) {
        toast.error(t('matrix.missingHypothesesEvidence'));
        return;
      }

      // Try to get existing matrix or create new one
      try {
        const matrixResponse = await matrixService.getMyMatrices();
        const existingMatrix = matrixResponse.data.find(m => m.mission_id === parseInt(missionId));

        if (existingMatrix) {
          const fullMatrix = await matrixService.getMatrix(existingMatrix.id);
          setMatrix(fullMatrix.data);
          setBriefingObert(!fullMatrix.data.is_started);
          // Initialize form fields from existing data
          setAssessment(fullMatrix.data.conclusion || '');
          setSelectedHypothesis(fullMatrix.data.most_likely_hypothesis_key || '');
          setDiagnosticityRanking(fullMatrix.data.diagnosticity_ranking || []);
        } else {
          // Create new matrix
          const newMatrix = await matrixService.createMatrix({ mission_id: parseInt(missionId) });
          const fullMatrix = await matrixService.getMatrix(newMatrix.data.id);
          setMatrix(fullMatrix.data);
        }

      } catch (error) {
        let message = t('matrix.errorInitializingMatrix');
        if (error.response?.data?.detail) {
          if (Array.isArray(error.response.data.detail)) {
            message = error.response.data.detail.map(err => err.msg).join(', ');
          } else {
            message = error.response.data.detail;
          }
        }
        toast.error(message);
        console.error('Matrix error:', error);
      }
    } catch (error) {
      toast.error(t('matrix.errorLoadingMissionDetails'));
    } finally {
      setLoading(false);
    }
  };

  const updateCellRating = async (cellId, rating) => {
    if (!cellId) {
      toast.error(t('matrix.errorCellNotFound'));
      return;
    }

    // La valoració es pinta a la pantalla ABANS de demanar-la al servidor.
    //
    // Els botons de ràdio són controlats: mentre l'estat no canviï, React
    // desfà el que ha fet el navegador. Esperant la resposta, cada pulsació
    // es veia dues vegades —el botó es marcava, tornava enrere i saltava
    // endavant quan arribava la resposta—, i amb les fletxes del teclat, que
    // recorren l'escala sencera, això passava quatre vegades seguides. És la
    // interacció que més es repeteix de tot l'examen: 84 cel·les.
    const anterior = matrix?.cells?.find(cell => cell.id === cellId)?.rating ?? null;
    const pintaValoracio = (valor) => setMatrix(prevMatrix => (prevMatrix ? {
      ...prevMatrix,
      cells: prevMatrix.cells.map(cell =>
        cell.id === cellId
          ? { ...cell, rating: valor }
          : cell
      ),
    } : prevMatrix));

    pintaValoracio(rating);

    try {
      const { data } = await matrixService.updateCell(matrix.id, cellId, { rating });

      // La resposta porta la puntuació recalculada pel servidor, de manera que la
      // fila de puntuacions es refresca a l'instant sense que el navegador hagi
      // de saber la regla de l'ACH ni hagi de tornar a demanar la matriu sencera.
      setMatrix(prevMatrix => (prevMatrix ? {
        ...prevMatrix,
        scores: data?.scores || prevMatrix.scores,
      } : prevMatrix));

      // AQUÍ NO HI VA CAP NOTIFICACIÓ, i és una decisió (04/10/2026).
      //
      // Hi havia un «Valoració actualitzada» per cel·la. Amb una matriu de
      // 5×10 són cinquanta aparicions per examen, més cada canvi d'opinió, i
      // amb el teclat una per tecla mentre es recorre l'escala. Portava
      // `id: 'valoracio'` perquè es reemplacés en comptes d'apilar-se, cosa que
      // no treia el problema: el convertia en una caixa que es refrescava a
      // dalt a la dreta durant tot l'exercici.
      //
      // I no deia res que la pantalla no digués ja. `pintaValoracio()` acaba de
      // pintar la cel·la amb el color de la valoració, i la fila de puntuacions
      // s'ha recalculat quatre línies més amunt amb la resposta del servidor.
      // Si el desat falla, el `catch` torna la cel·la al que hi havia i allà sí
      // que hi ha un avís, perquè el motiu —temps exhaurit, exercici congelat—
      // no es veu enlloc.
      //
      // És el mateix criteri que ja seguien les altres tres coses d'aquesta
      // pantalla: l'avís dels cinc minuts és una finestra, la pèrdua de
      // connexió viu a la barra mentre duri, i el desat automàtic desa amb
      // `silent`. Notificació només per al que no es veu.
    } catch (error) {
      // El servidor l'ha rebutjada —temps exhaurit, exercici congelat— i la
      // pantalla no pot dir el contrari: es torna al que hi havia.
      pintaValoracio(anterior);

      let message = t('matrix.errorUpdatingCell');
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          message = error.response.data.detail.map(err => err.msg).join(', ');
        } else {
          message = error.response.data.detail;
        }
      }
      toast.error(message);
    }
  };

  // `silent` l'utilitza el desat automàtic: desa sense empaperar la pantalla de
  // notificacions cada cinc segons.
  const updateMatrixAnalysis = async ({ silent = false } = {}) => {
    setEstatDesat('desant');
    try {
      await matrixService.updateMatrix(matrix.id, {
        conclusion: assessment,
        most_likely_hypothesis_key: selectedHypothesis || null,
        diagnosticity_ranking: diagnosticityRanking
      });
      // S'anota TOT el que s'acaba de desar. Si només s'hi posés la conclusió,
      // la comprovació «hi ha res per desar?» del desat automàtic no deixaria
      // mai de ser certa per als altres dos camps i desaria cada cinc segons.
      setMatrix(prev => (prev ? {
        ...prev,
        conclusion: assessment,
        most_likely_hypothesis_key: selectedHypothesis || null,
        diagnosticity_ranking: diagnosticityRanking,
      } : prev));
      setEstatDesat('desat');
      if (!silent) toast.success(t('matrix.analysisSaved'), { duration: 1500 });
    } catch (error) {
      setEstatDesat('error');
      if (!silent) toast.error(t('matrix.errorSavingAnalysis'));
    }
  };

  const textEstatDesat = () => {
    if (estatDesat === 'pendent') return t('matrix.unsaved');
    if (estatDesat === 'desant') return t('matrix.saving');
    if (estatDesat === 'desat') return t('matrix.saved');
    if (estatDesat === 'error') return t('matrix.saveFailed');
    return '';
  };

  const handleStartMission = async () => {
    try {
      await matrixService.startMission(matrix.id);
      toast.success(t('matrix.missionStarted') || 'Mission started! Timer is now running.');
      // Refresh matrix to get updated data with deadline
      const response = await matrixService.getMatrix(matrix.id);
      setMatrix(response.data);
      setBriefingObert(false);
      // Amunt de tot, i no és cosmètic.
      //
      // El botó de començar és a mitja pàgina. En prémer-lo passen dues coses
      // alhora: l'enunciat es plega i la graella apareix. El navegador conserva
      // el desplaçament que hi havia, i amb el contingut de sobre encongit,
      // aquell mateix desplaçament cau enmig de la matriu: l'exercici començava
      // amb el rellotge en marxa, la barra d'estat fora de la vista i unes files
      // qualssevol al davant.
      window.scrollTo({ top: 0, behavior: 'auto' });
    } catch (error) {
      // El servidor sap per què no es pot començar —la missió encara no s'ha
      // obert, o ja s'ha tancat— i l'alumne ho ha de poder llegir. Amb el
      // missatge genèric, una finestra tancada era indistingible d'una avaria.
      const detall = error.response?.data?.detail;
      toast.error(
        (typeof detall === 'string' && detall) ||
        t('matrix.errorStartingMission') || 'Failed to start mission'
      );
    }
  };

  // NOTA (03/08/2026): aquí hi havia handleAutoSubmit, que quan el compte enrere
  // del navegador arribava a zero lliurava la matriu des del client. Era l'ÚNIC
  // tancament per temps que existia, i per tant només funcionava si l'alumne
  // tenia la pestanya oberta en aquell instant precís. Ara el venciment el fa el
  // servidor (app/core/timing.py) i una tasca periòdica el garanteix encara que
  // no hi hagi cap navegador connectat.

  const submitMatrix = async () => {
    try {
      // First save the analysis data
      await updateMatrixAnalysis();
      // Then submit the matrix
      await matrixService.submitMatrix(matrix.id);
      toast.success(t('matrix.matrixSubmittedSuccess'));
      // Refresh matrix
      const response = await matrixService.getMatrix(matrix.id);
      setMatrix(response.data);
      setShowFinishModal(false);
    } catch (error) {
      toast.error(t('matrix.errorSubmittingMatrix'));
    }
  };

  // La hipòtesi de l'alumne s'escriu en l'idioma que té a la pantalla, i prou.
  //
  // Fins al 06/08/2026 aquesta finestra demanava quatre camps —descripció curta
  // i llarga, en anglès i en català— enmig d'un examen cronometrat. Qui redacta
  // el contingut bilingüe és l'instructor, des de la pantalla de la missió; a
  // l'alumne se li demana el que ha de dir, una frase, en la llengua en què
  // està treballant. El camp curt i el llarg es desen iguals: en una hipòtesi
  // d'una frase, distingir-los no vol dir res.
  const handleAddUserHypothesis = async () => {
    const text = novaHipotesi.trim();
    if (!text) {
      toast.error(t('matrix.hypothesisRequired'));
      return;
    }

    const idioma = (i18n.language || '').startsWith('en') ? 'en' : 'ca';
    const ampliacio = novaAmpliacio.trim();

    try {
      // La frase és el que es veurà a la columna; l'ampliació, el que hi haurà
      // darrere del botó «Veure». Si no n'escriu cap, la descripció llarga és
      // la mateixa frase i el botó no apareix: no té sentit obrir una fitxa
      // per llegir el que ja es llegeix a la capçalera.
      await matrixService.createUserHypothesis(matrix.id, {
        short_description: { [idioma]: text },
        description: { [idioma]: ampliacio || text },
      });

      toast.success(t('matrix.hypothesisAdded'));

      // Refresh matrix to get new hypothesis and cells
      const response = await matrixService.getMatrix(matrix.id);
      setMatrix(response.data);

      setNovaHipotesi('');
      setNovaAmpliacio('');
      setShowAddHypothesisModal(false);
    } catch (error) {
      toast.error(t('matrix.errorAddingHypothesis'));
    }
  };

  // Get merged hypotheses (mission + user hypotheses)
  const getAllHypotheses = () => {
    return matrix?.merged_hypotheses || mission?.hypotheses || [];
  };

  // Clau no ambigua d'una hipòtesi: 'h104' (de la missió) o 'u12' (de l'alumne).
  //
  // Aparellar cel·les i hipòtesis només pel número era un error latent: les dues
  // taules tenen seqüències d'identificadors independents, així que una hipòtesi
  // de missió amb id 3 i una de l'alumne amb id 3 es confonien i les valoracions
  // d'una sortien a la columna de l'altra. El servidor ja envia la clau; el
  // `|| h${id}` només cobreix respostes antigues que encara no la porten.
  const hypothesisKey = (hypothesis) =>
    hypothesis?.key || (hypothesis?.is_user_created ? `u${hypothesis.id}` : `h${hypothesis?.id}`);

  const findCell = (hypothesis, evidenceId) => {
    const clau = hypothesisKey(hypothesis);
    return matrix?.cells?.find(
      c => (c.hypothesis_key || `h${c.hypothesis_id}`) === clau && c.evidence_id === evidenceId
    );
  };

  // NOTA (03/08/2026): aquí hi havia calculateHypothesisScore i getBestHypothesis,
  // que implementaven la regla de l'ACH (CC/C/N = 0, I = -1, II = -2) al
  // navegador. La mateixa regla estava copiada literalment a SubmissionReview.js
  // i una tercera vegada a ARGOS. Ara la calcula el servidor
  // (backend/app/core/scoring.py) i arriba amb la matriu i amb cada cel·la
  // desada, de manera que la fila de puntuacions es refresca igual de ràpid.

  // Puntuació d'una hipòtesi segons el servidor. La clau ('h104' o 'u12') no és
  // ambigua: les hipòtesis de la missió i les de l'alumne poden repetir número.
  const getScore = (hypothesis) => {
    const entrada = (matrix?.scores || []).find(s => s.key === hypothesisKey(hypothesis));
    return entrada ? entrada.score : 0;
  };

  // La diagnosticitat es marca a la fila de l'evidència.
  //
  // Fins al 06/08/2026 era una llista de dues columnes, sota la matriu, on
  // s'arrossegaven evidències d'un costat a l'altre. Però una evidència és
  // diagnòstica quan les seves valoracions són DIFERENTS entre hipòtesis: es
  // reconeix mirant la fila. Tenint-ho en una llista a part, l'alumne ho havia
  // de recordar en comptes de veure-ho. L'ordre és el de marcatge.
  const marcaDiagnostica = (evidenceId) => {
    if (diagnosticityRanking.includes(evidenceId)) {
      setDiagnosticityRanking(diagnosticityRanking.filter(id => id !== evidenceId));
      return;
    }
    if (diagnosticityRanking.length >= 3) {
      toast(t('matrix.diagnosticFull'), { id: 'diagnostica-plena' });
      return;
    }
    setDiagnosticityRanking([...diagnosticityRanking, evidenceId]);
  };

  // Posició dins de la tria (1, 2 o 3), o null si no hi és.
  const posicioDiagnostica = (evidenceId) => {
    const i = diagnosticityRanking.indexOf(evidenceId);
    return i === -1 ? null : i + 1;
  };

  // Format time remaining (seconds to MM:SS or HH:MM:SS)
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '--:--';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Color del cronòmetre segons el temps que queda. Els colors són al CSS: aquí
  // només es diu en quin dels tres trams som.
  const classeTemps = () => {
    if (timeRemaining === null || timeRemaining === undefined) return 'temps-neutre';
    if (timeRemaining > 600) return 'temps-ok';
    if (timeRemaining > AVIS_SEGONS) return 'temps-avis';
    return 'temps-critic';
  };

  // L'estat de l'exercici, traduït.
  //
  // Fins al 05/08/2026 sortia de fer `status.replace('_',' ')` amb les inicials
  // en majúscula, o sigui «In Progress» i «Time Expired» en anglès encara que
  // la interfície fos en català —i justament a la pantalla de l'examen, que és
  // on l'alumne mira l'estat. Congelat no és un estat de la base de dades
  // (segueix sent `in_progress` amb `frozen_at`), però per a qui mira la
  // pantalla sí que ho és, i és el que ha de llegir.
  const etiquetaEstat = (estat) => t(`matrix.statuses.${estat}`, { defaultValue: estat });

  if (loading) return <div className="loading">{t('matrix.loadingMatrix')}</div>;
  if (!mission) return <div className="card">{t('matrix.errorLoadingMissionDetails')}</div>;

  // If mission exists but no matrix and no hypotheses/evidence, show informative message
  if (!matrix && (!mission.hypotheses?.length || !mission.evidence?.length)) {
    return (
      <div>
        <h1>{mission.title} - {t('matrix.achMatrix')}</h1>
        <div className="card">
          <h3>{t('matrix.matrixNotAvailable')}</h3>
          <p>{t('matrix.missionNotConfigured')}</p>
          <p>{t('matrix.instructorNeedsSetup')}</p>
          <p><strong>{t('matrix.missing')}:</strong></p>
          <ul>
            {!mission.hypotheses?.length && <li>{t('matrix.hypotheses')}</li>}
            {!mission.evidence?.length && <li>{t('matrix.evidence')}</li>}
          </ul>
        </div>
      </div>
    );
  }

  if (!matrix) return <div className="card">{t('matrix.errorLoadingMatrix')}</div>;

  // Check if matrix has cells
  if (!matrix.cells || matrix.cells.length === 0) {
    return (
      <div>
        <h1>{mission.title} - {t('matrix.achMatrix')}</h1>
        <div className="card">
          <h3>{t('matrix.matrixNotReady')}</h3>
          <p>{t('matrix.matrixCreatedBeforeConfig')}</p>
          <p>{t('matrix.matrixNeedsRecreation')}</p>
          <p><strong>{t('matrix.contactInstructorReset')}</strong></p>
        </div>
      </div>
    );
  }

  const isSubmitted = matrix.status === 'submitted' || matrix.status === 'time_expired';
  // Congelada: l'exercici es conserva sencer però no s'hi pot tocar res fins que
  // l'instructor el reobri. No és el mateix que lliurat, i la pantalla ho ha de
  // distingir perquè l'alumne sàpiga que la seva feina no s'ha perdut.
  const isFrozen = Boolean(matrix.is_frozen);
  const isReadOnly = isSubmitted || isFrozen;

  // La pantalla prèvia: l'alumne que encara no ha començat. L'instructor no la
  // té —a ell se li ensenya la matriu, que és la previsualització— i per això
  // tota la graella de dues columnes penja d'aquesta condició i no d'una altra.
  const materialPrevi = !matrix.is_started && !isSubmitted && !isInstructor;

  // El relat es dibuixa igual en tots dos casos, però NO al mateix lloc: a la
  // pantalla prèvia va dins de la graella, amb el material al costat, i a la
  // resta va a tot l'ample sobre la matriu. Per això és una variable: si fos
  // JSX repetit, un dels dos acabaria divergint.
  const relat = (
    <details
      className="mission-briefing"
      open={briefingObert}
      onToggle={(e) => setBriefingObert(e.target.open)}
    >
      <summary className="briefing-summary">{t('matrix.missionBriefing')}</summary>
      <div className="story-content">
        <ReactMarkdown>{getLocalizedText(mission.story || mission.description)}</ReactMarkdown>
      </div>
    </details>
  );

  // L'avís va a tot l'ample sota la barra, fora de les dues columnes: diu quants
  // minuts tindrà i és el primer que s'ha de llegir, no una nota al marge de la
  // columna de lectura.
  const avisSenseComencar = !matrix.is_started && !isSubmitted ? (
    <div className="avis-bloc avis-bloc--info">
      <h3>{t('matrix.readyToStart')}</h3>
      <p>{t('matrix.notStartedMessage', { minutes: matrix.mission_time_limit_minutes })}</p>
    </div>
  ) : null;

  return (
    <div className="matrix-container">
      {/* A l'alumne, ni copiar ni enganxar: l'anàlisi l'ha d'escriure ell.
          A l'instructor no se li bloqueja res. */}
      <BlocatgeCopia actiu={!isInstructor} />
      {isFrozen && (
        <div className="avis-bloc avis-bloc--congelat">
          <h3>{t('matrix.frozenTitle')}</h3>
          <p>
            {t('matrix.frozenMessage')}
            <strong>{formatTime(timeRemaining)}</strong>
            {t('matrix.frozenMessageEnd')}
          </p>
        </div>
      )}
      {descriptionModal && (
        <div className="modal-overlay" onClick={closeDescriptionModal}>
          <div className="fitxa" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="fitxa-cap">
              <span className="fitxa-tipus">{descriptionModal.tipus}</span>
              <button className="modal-close" onClick={closeDescriptionModal} aria-label={t('common.close')}>×</button>
            </div>

            <h3 className="fitxa-titol">
              <span className="fitxa-codi">{descriptionModal.code}</span>
              {descriptionModal.titol}
            </h3>

            {descriptionModal.text && descriptionModal.text !== descriptionModal.titol && (
              <p className="fitxa-text">{descriptionModal.text}</p>
            )}

            {(descriptionModal.font || descriptionModal.fiabilitat !== null) && (
              <dl className="fitxa-dades">
                {descriptionModal.font && (
                  <>
                    <dt>{t('matrix.source')}</dt>
                    <dd>{descriptionModal.font}</dd>
                  </>
                )}
                {descriptionModal.fiabilitat !== null && (
                  <>
                    <dt>{t('matrix.reliability')}</dt>
                    <dd>{t('matrix.reliabilityValue', {
                      value: descriptionModal.fiabilitat.toLocaleString(i18n.language),
                    })}</dd>
                  </>
                )}
              </dl>
            )}
          </div>
        </div>
      )}
      {/* La barra de l'examen.
          El cronòmetre vivia dins de la capçalera de la pàgina i no era fix:
          amb un enunciat de dos mil caràcters pel mig, quan l'alumne arribava a
          la matriu ja feia estona que havia quedat amunt. Durant un examen,
          saber quant temps queda ha de ser mirar, no recordar. La barra porta
          els quatre estats: sense començar, en curs, congelat i lliurat. */}
      <div className="exam-bar">
        <div className="exam-bar-fila">
          <h1 className="exam-bar-title">{mission.title}</h1>

          <span className={`status-badge status-${(isFrozen ? 'frozen' : matrix.status).replace('_', '-')}`}>
            {etiquetaEstat(isFrozen ? 'frozen' : matrix.status)}
          </span>

          {matrix.is_started && !isSubmitted && (
            <span className={`exam-bar-timer ${isFrozen ? 'temps-neutre' : classeTemps()}`}>
              {formatTime(timeRemaining)}
            </span>
          )}

          {!isOnline && (
            <span className="exam-bar-avis">{t('matrix.connectionLost')}</span>
          )}

          <div className="exam-bar-accions">
            {!matrix.is_started && !isSubmitted && (
              <button onClick={handleStartMission} className="btn btn-primary start-mission-btn">
                {t('matrix.startMission')} ({matrix.mission_time_limit_minutes} {t('common.minutes')})
              </button>
            )}

            {matrix.is_started && !isReadOnly && (
              <button onClick={() => setShowFinishModal(true)} className="btn btn-success submit-btn">
                {t('matrix.finishMission')}
              </button>
            )}
          </div>
        </div>

        {/* La tira de puntuacions.
            Apareix quan la matriu ha marxat de la pantalla, que és quan
            l'alumne està escrivint la conclusió. L'argument de l'ACH és
            «aquesta hipòtesi és la que acumula menys inconsistències»: el
            material per dir-ho ha de ser a la vista mentre s'escriu, i per
            això no cal la taula sencera. */}
        {matrix.is_started && escrivint && (
          <div className="tira-puntuacions">
            {getAllHypotheses().map((h) => {
              const clau = hypothesisKey(h);
              return (
                <span
                  key={clau}
                  className={`tira-item${selectedHypothesis === clau ? ' is-selected' : ''}`}
                  title={getShortDescription(h, i18n.language)}
                >
                  <strong>{h.code}</strong>
                  <span className="tira-punts">{getScore(h)}</span>
                </span>
              );
            })}
            <button
              type="button"
              className="text-link tira-tornar"
              onClick={() => taulaRef.current?.scrollIntoView({ block: 'start' })}
            >
              {t('matrix.backToMatrix')}
            </button>
          </div>
        )}
      </div>

      {/* L'enunciat, plegable: la feina real és llegir una evidència, tornar a
          l'enunciat i comparar, i amb l'enunciat sencer sempre obert a sobre de
          la matriu això eren desenes de pujades i baixades amb el rellotge
          corrent. <details> ho fa sol i funciona amb el teclat.

          A la pantalla prèvia no va aquí: va dins de `previ-doble`, amb el
          material al costat. */}
      {!materialPrevi && relat}

      {avisSenseComencar}

      {/* Abans de començar NO es veu la matriu.
          És un criteri pedagògic, no d'espai: amb la graella a la vista i el
          rellotge aturat, l'alumne la resol amb calma i només prem «començar»
          quan ja ho té clar. L'exercici de l'ACH és fer el creuament sota la
          pressió del temps; el que sí que pot llegir abans és el material —el
          relat, les hipòtesis i les evidències—, que és el que tenia a la
          pantalla de la missió.

          A l'instructor sí que se li ensenya: «Previsualitzar anàlisi» serveix
          precisament per comprovar que la matriu està ben muntada. */}
      {materialPrevi ? (
        <div className="previ-doble">
        {relat}
        <div className="material-previ">
          <h2 className="carril-titol">{t('missionDetail.whatYouWillGet')}</h2>
          <div className="material-previ-graella">
            <section>
              <h3>{t('matrix.hypotheses')} <span className="count-subtle">({getAllHypotheses().length})</span></h3>
              <ul>
                {getAllHypotheses().map((h) => (
                  <li key={hypothesisKey(h)}>
                    <span className="material-codi">{h.code}</span>
                    <span className="material-text">{getShortDescription(h, i18n.language)}</span>
                    {getFullDescription(h, i18n.language)
                      && getFullDescription(h, i18n.language) !== getShortDescription(h, i18n.language) && (
                      <button
                        className="matrix-info-btn"
                        onClick={() => obreFitxa(t('matrix.hypothesis'), h)}
                        title={t('matrix.viewFullDescription')}
                      >
                        {t('matrix.more')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <h3>{t('matrix.evidence')} <span className="count-subtle">({mission.evidence?.length || 0})</span></h3>
              <ul>
                {/* L'evidència sempre té fitxa: encara que la descripció
                    llarga sigui la mateixa, hi ha la font i la fiabilitat, que
                    és el que l'alumne ha de mirar per saber quant hi pesa. */}
                {mission.evidence?.map((e) => (
                  <li key={e.id}>
                    <span className="material-codi">{e.code}</span>
                    <span className="material-text">{getShortDescription(e, i18n.language)}</span>
                    <button
                      className="matrix-info-btn"
                      onClick={() => obreFitxa(t('matrix.evidence'), e)}
                      title={t('matrix.viewFullDescription')}
                    >
                      {t('matrix.more')}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
        </div>
      ) : (
      <div className="ach-analysis-section">
        <div className="analysis-header">
          <div className="analysis-title">
            <h2>{t('matrix.achAnalysisMatrix')}</h2>
            {matrix.is_started && (
              <p className="analysis-subtitle">
                {t('matrix.diagnosticHint', { count: diagnosticityRanking.length })}
              </p>
            )}
          </div>
          <div className="legend-toggle">
            {matrix.is_started && !isReadOnly && (
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddHypothesisModal(true)}
                style={{ marginRight: '10px' }}
              >
                {t('matrix.addHypothesis') || 'Add Hypothesis'}
              </button>
            )}
            <button className="legend-btn" onClick={() => setShowLegend(!showLegend)}>
              {showLegend ? t('matrix.hideLegend') : t('matrix.showLegend')}
            </button>
          </div>
        </div>

        <div className="matrix-layout">
          <div className="matrix-main">
            <div className="table-container" ref={taulaRef}>
              <table className="matrix-table">
                <thead>
                  <tr>
                    <th className="evidence-header">{t('matrix.evidenceHypothesis')}</th>
                    {getAllHypotheses().map((h) => (
                      <th key={hypothesisKey(h)} className={`hypothesis-header ${h.is_user_created ? 'user-hypothesis' : ''}`}>
                        <div className="hypothesis-cell">
                          <span className="hypothesis-code">
                            {h.code}
                            {h.is_user_created && (
                              <span className="user-badge" title={t('matrix.ownHypothesis')}>*</span>
                            )}
                          </span>
                          <span className="hypothesis-desc">
                            {getShortDescription(h, i18n.language, 30)}
                          </span>
                          {getFullDescription(h, i18n.language) !== getShortDescription(h, i18n.language) && getFullDescription(h, i18n.language) && (
                            <button
                              className="matrix-info-btn"
                              onClick={() => obreFitxa(t('matrix.hypothesis'), h)}
                              title={t('matrix.viewFullDescription')}
                            >
                              {t('matrix.more')}
                            </button>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {mission.evidence?.map((evidence, fila) => {
                    const posicio = posicioDiagnostica(evidence.id);
                    return (
                    <React.Fragment key={evidence.id}>
                    <tr className={posicio ? 'fila-diagnostica' : ''}>
                      <td className="evidence-cell">
                        <div className="evidence-content">
                          {/* La marca de diagnosticitat va a la fila perquè la
                              diagnosticitat és una propietat de la fila: una
                              evidència distingeix entre hipòtesis quan les
                              seves valoracions no són totes iguals. */}
                          <button
                            type="button"
                            className={`marca-diagnostica${posicio ? ' is-marked' : ''}`}
                            onClick={() => marcaDiagnostica(evidence.id)}
                            disabled={isReadOnly || !matrix.is_started}
                            aria-pressed={Boolean(posicio)}
                            title={posicio
                              ? t('matrix.diagnosticUnmark', { code: evidence.code })
                              : t('matrix.diagnosticMark', { code: evidence.code })}
                          >
                            {posicio || ''}
                          </button>
                          <strong className="evidence-code">{evidence.code}</strong>
                          <span className="evidence-desc">
                            {getShortDescription(evidence, i18n.language)}
                          </span>
                          {getShortDescription(evidence, i18n.language) && (
                            <button
                              className="matrix-info-btn"
                              onClick={() => obreFitxa(t('matrix.evidence'), evidence)}
                              title={t('matrix.viewFullDescription')}
                            >
                              {t('matrix.more')}
                            </button>
                          )}
                        </div>
                      </td>
                      {getAllHypotheses().map((hypothesis) => {
                        const cell = findCell(hypothesis, evidence.id);
                        const bloquejada = isReadOnly || !cell?.id || !matrix.is_started;
                        const grup = `cel-${cell?.id || `${evidence.id}-${hypothesisKey(hypothesis)}`}`;

                        return (
                          <td key={`${evidence.id}-${hypothesisKey(hypothesis)}`} className="matrix-cell">
                            {/* Botons de ràdio de debò, no un giny propi: les
                                fletxes del teclat recorren l'escala soles i el
                                focus es veu sense haver-lo de programar. */}
                            <div
                              className="rating-escala"
                              role="radiogroup"
                              aria-label={t('matrix.ratingFor', {
                                evidence: evidence.code,
                                hypothesis: hypothesis.code,
                              })}
                            >
                              {VALORACIONS.map((valor) => {
                                const triada = cell?.rating === valor;
                                return (
                                  <label
                                    key={valor}
                                    className={`rating-option${triada ? ` is-selected rating-${valor}` : ''}${bloquejada ? ' is-disabled' : ''}`}
                                    title={t(NOM_VALORACIO[valor])}
                                  >
                                    <input
                                      type="radio"
                                      name={grup}
                                      value={valor}
                                      checked={triada}
                                      disabled={bloquejada}
                                      onChange={() => cell?.id && updateCellRating(cell.id, valor)}
                                    />
                                    <span>{valor}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="score-row">
                    <td className="score-header">
                      <div className="score-label">
                        <strong>{t('matrix.hypothesisScore')}</strong>
                      </div>
                    </td>
                    {/* Aquí NO s'assenyala la millor hipòtesi.
                        Fins al 06/08/2026 la columna amb la puntuació menys
                        negativa sortia marcada com a «MÉS VIABLE» i el rètol de
                        la fila deia «menor és millor». Les dues coses donaven
                        feta la part que l'alumne ha de fer: llegir el patró
                        d'inconsistències i decidir. La puntuació hi és; la
                        conclusió la treu ell, a la fila de sota. */}
                    {getAllHypotheses().map((hypothesis) => (
                      <td key={`score-${hypothesisKey(hypothesis)}`} className="score-cell">
                        <div className="score-value">
                          <span className="score-number">{getScore(hypothesis)}</span>
                        </div>
                      </td>
                    ))}
                  </tr>

                  {/* La conclusió es tria a la matriu.
                      Era un desplegable dos mil píxels més avall de la fila de
                      puntuacions que l'ha de fonamentar. Aquí, just a sota del
                      número, es veu quan el judici de l'alumne i l'aritmètica
                      no diuen el mateix —que és el moment que l'ACH vol
                      provocar: la puntuació és una ajuda, no la resposta. */}
                  {matrix.is_started && (
                    <tr className="conclusio-row">
                      <td className="score-header">
                        <div className="score-label">
                          <strong>{t('matrix.myConclusion')}</strong>
                          <small>{t('matrix.myConclusionHint')}</small>
                        </div>
                      </td>
                      {getAllHypotheses().map((hypothesis) => {
                        const clau = hypothesisKey(hypothesis);
                        const triada = selectedHypothesis === clau;

                        return (
                          <td key={`conclusio-${clau}`} className="conclusio-cell">
                            <label className={`conclusio-opcio${triada ? ' is-selected' : ''}${isReadOnly ? ' is-disabled' : ''}`}>
                              <input
                                type="radio"
                                name="conclusio"
                                value={clau}
                                checked={triada}
                                disabled={isReadOnly}
                                onChange={() => setSelectedHypothesis(clau)}
                              />
                              <span>{hypothesis.code}</span>
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          </div>

        </div>
      </div>
      )}

      {/* La correcció de l'instructor.
          Va abans de la redacció i just després de la matriu, que és el primer
          que mira qui torna a entrar a una entrega ja tancada. Fins al
          05/08/2026 l'alumne lliurava i no rebia mai res des de l'aplicació. */}
      {isSubmitted && matrix.graded_at && (
        <div className="analysis-submission-section instructor-feedback">
          <div className="submission-header">
            <h2>{t('matrix.instructorFeedback')}</h2>
          </div>
          <div className="submission-card">
            {matrix.grade !== null && matrix.grade !== undefined && (
              <p className="feedback-grade">
                <strong>{t('matrix.grade')}:</strong> {matrix.grade} / 10
              </p>
            )}
            {matrix.feedback && (
              <p className="feedback-text" style={{ whiteSpace: 'pre-wrap' }}>{matrix.feedback}</p>
            )}
            <p className="text-muted">
              {t('matrix.gradedBy', {
                username: matrix.graded_by?.username || '',
                date: mostraDataHora(matrix.graded_at),
              })}
            </p>
          </div>
        </div>
      )}

      {/* La redacció.
          Des del 06/08/2026 és l'ÚNICA cosa que hi ha sota la matriu: la tria
          de la hipòtesi i la marca de diagnosticitat s'han mogut a dins de la
          taula, que és on és la informació que les fonamenta.

          Aquí hi havia quatre textos que deien la mateixa cosa —un títol de
          secció, un títol de fitxa, un subtítol, una instrucció emmarcada i un
          peu—, tots demanant «una avaluació completa del teu procés analític».
          Ara n'hi ha un que diu què s'ha d'explicar, i una llista plegable per
          a qui no sap per on començar. */}
      {matrix.is_started && (
        <div className="analysis-submission-section" ref={redaccioRef}>
          <div className="submission-header">
            <h2>{isSubmitted ? t('matrix.submittedAnalysis') : t('matrix.yourAnalysis')}</h2>
            {!isReadOnly && <span className={`estat-desat estat-desat--${estatDesat}`}>{textEstatDesat()}</span>}
          </div>

          <div className="assessment-card">
            {!isReadOnly && (
              <>
                <p className="assessment-guia">{t('matrix.analysisGuide')}</p>
                <details className="assessment-ajuda">
                  <summary>{t('matrix.analysisChecklistTitle')}</summary>
                  <ul>
                    <li>{t('matrix.analysisChecklist1')}</li>
                    <li>{t('matrix.analysisChecklist2')}</li>
                    <li>{t('matrix.analysisChecklist3')}</li>
                    <li>{t('matrix.analysisChecklist4')}</li>
                  </ul>
                </details>
              </>
            )}

            <textarea
              value={assessment}
              onChange={(e) => setAssessment(e.target.value)}
              rows="12"
              className="assessment-textarea"
              disabled={isReadOnly}
              readOnly={isReadOnly}
              aria-label={t('matrix.yourAnalysis')}
            />

            <div className="assessment-footer">
              <span className="char-count">{assessment.length} {t('matrix.characters')}</span>
              {estatDesat === 'error' && !isReadOnly && (
                <button onClick={() => updateMatrixAnalysis()} className="text-link">
                  {t('matrix.retrySave')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* L'avís dels cinc minuts.
          Sense creu ni tancament clicant a fora: s'ha de prémer el botó. Si
          mentrestant el temps s'acaba, desapareix sol —ja no hi ha res a
          avisar. */}
      {avisTempsObert && matrix.status === 'in_progress' && !isFrozen && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--estret avis-temps" role="alertdialog" aria-modal="true">
            <div className="modal-body">
              <h3>{t('matrix.fiveMinutesTitle')}</h3>
              <p>{t('matrix.fiveMinutesWarning')}</p>
              <div className="modal-accions">
                <button className="btn btn-primary" onClick={() => setAvisTempsObert(false)} autoFocus>
                  {t('common.accept')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend Modal */}
      {showLegend && (
        <div className="modal-overlay" onClick={() => setShowLegend(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>{t('matrix.ratingLegend')}</h4>
              <button className="modal-close" onClick={() => setShowLegend(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="legend-items">
                <div className="legend-item">
                  <span className="legend-badge rating-CC">CC</span>
                  <span>{t('matrix.veryConsistent')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge rating-C">C</span>
                  <span>{t('matrix.consistent')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge rating-N">N</span>
                  <span>{t('matrix.neutral')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge rating-I">I</span>
                  <span>{t('matrix.inconsistent')}</span>
                </div>
                <div className="legend-item">
                  <span className="legend-badge rating-II">II</span>
                  <span>{t('matrix.veryInconsistent')}</span>
                </div>
              </div>
              <p className="legend-note">
                {t('matrix.rateEvidenceDescription')}
              </p>

              <div className="scoring-info">
                <h5>{t('matrix.achScoring')}</h5>
                <div className="scoring-rules">
                  <div className="scoring-rule">
                    <span className="legend-badge rating-CC">CC</span>
                    <span className="legend-badge rating-C">C</span>
                    <span className="legend-badge rating-N">N</span>
                    <span className="rule-text">= 0 {t('matrix.points')}</span>
                  </div>
                  <div className="scoring-rule">
                    <span className="legend-badge rating-I">I</span>
                    <span className="rule-text">= -1 {t('matrix.point')}</span>
                  </div>
                  <div className="scoring-rule">
                    <span className="legend-badge rating-II">II</span>
                    <span className="rule-text">= -2 {t('matrix.points')}</span>
                  </div>
                </div>
                <p className="scoring-note">
                  {t('matrix.highestScoreDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Finish Mission Confirmation Modal */}
      {showFinishModal && (
        <div className="modal-overlay" onClick={() => setShowFinishModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('matrix.submitAnalysis') || 'Submit Analysis'}</h3>
              <button className="modal-close" onClick={() => setShowFinishModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '1.1em', marginBottom: '20px' }}>
                {t('matrix.confirmSubmitMessage') || 'This will submit your analysis and you won\'t be able to edit anymore. Are you sure?'}
              </p>
              <div className="modal-accions">
                <button onClick={() => setShowFinishModal(false)} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
                <button onClick={submitMatrix} className="btn btn-primary">
                  {t('common.yesSubmit')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Hypothesis Modal */}
      {showAddHypothesisModal && (
        <div className="modal-overlay" onClick={() => setShowAddHypothesisModal(false)}>
          <div className="modal-content modal-content--estret" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('matrix.addYourHypothesis')}</h3>
              <button className="modal-close" onClick={() => setShowAddHypothesisModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="field-description">
                {t('matrix.addHypothesisDescription')}
              </p>

              <div className="camp-modal">
                <label htmlFor="nova-hipotesi">{t('matrix.hypothesisOneLine')}</label>
                <input
                  id="nova-hipotesi"
                  type="text"
                  maxLength={120}
                  value={novaHipotesi}
                  onChange={(e) => setNovaHipotesi(e.target.value)}
                  placeholder={t('matrix.hypothesisPlaceholder')}
                  autoFocus
                />
              </div>

              <div className="camp-modal">
                <label htmlFor="nova-ampliacio">{t('matrix.hypothesisDetailLabel')}</label>
                <textarea
                  id="nova-ampliacio"
                  rows="4"
                  value={novaAmpliacio}
                  onChange={(e) => setNovaAmpliacio(e.target.value)}
                  placeholder={t('matrix.hypothesisDetailPlaceholder')}
                />
                <p className="camp-ajuda">{t('matrix.hypothesisDetailHelp')}</p>
              </div>

              <div className="modal-accions">
                <button onClick={() => setShowAddHypothesisModal(false)} className="btn btn-secondary">
                  {t('common.cancel')}
                </button>
                <button onClick={handleAddUserHypothesis} className="btn btn-primary" disabled={!novaHipotesi.trim()}>
                  {t('common.add')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Matrix;